/* Экран черновика: сверху шапка поста, дальше — тело текущего режима, внизу закреплённая
   полоса действий (её раскладка — decisions/DecisionPanel.module.css).

   ПОРЯДОК ЧТЕНИЯ ВМЕСТО ТРЁХ КОЛОНОК. Три колонки «исходник → превью → решение» расширяли
   экран и требовали горизонтальной прокрутки; решение убрано вниз, редактор — под кнопку,
   и на экране в каждый момент ровно то, что человек сейчас делает (решение заказчика
   26.08.2026). Колонок теперь не больше двух, и только в режиме правки.

   Раскладка текучая: ни одной фиксированной ширины, все сетки на minmax(0, …). Потолки у
   `.narrow` и `.wide` — не ширины, а границы читаемости:
     • 680px — колонка чтения: панель темы при 15px даёт ~90 знаков в строке, а пузырь
       сообщения всё равно упирается в 480px, и растягивать зону превью дальше нечем;
     • 1180px — два полотна рядом (правка и сравнение): по ~580px на полотно, то есть пузырь
       480px плюс поля зоны. Дальше полотна не расползаются — шире они не становятся точнее.

   `--screen-pad` объявлен здесь и читается полосой действий: она гасит его отрицательным
   полем, чтобы идти от края до края. Одно число на два файла, а не два числа врозь. */

.panel {
  --screen-pad: 24px;
  flex: 1;
  min-width: 0;
  /*
   * СТОЛБЕЦ ЦЕНТРИРУЕТСЯ, А НЕ ПРИЖИМАЕТСЯ ВЛЕВО. На 1920 панель занимала всю оставшуюся
   * ширину, а содержимое (превью — 680px) жалось к левому краю: справа оставалось полэкрана
   * пустоты, и взгляд каждый раз возвращался в угол.
   *
   * Предел ставится на ПАНЕЛЬ, а не на внутренние блоки, потому что иначе заголовок остался
   * бы у левого края, а превью уехало в середину — рассогласование хуже, чем пустота справа.
   * Предел один на все режимы: полоса действий бьётся в края панели, а «полоса шириной с
   * рабочую поверхность» и «полоса шириной со столбец текста» не могут быть правдой
   * одновременно.
   *
   * ПРЕДЕЛ СВОЙ — `--workspace-max`, а не общий с дашбордами `--surface-max`. Значение по
   * умолчанию у них одно (1400px), но обоснования разные: у дашбордов число измерено на
   * строке таблицы, которая шире 1400px рвёт связь подписи со значением; здесь строк таблицы
   * нет, а есть два полотна рядом — и на 2K лишние 200px идут им в ширину чтения. Поэтому
   * выше 1800px `--workspace-max` поднимается до 1600px, а `--surface-max` остаётся на 1400.
   *
   * Полоса действий бьётся в края ПАНЕЛИ отрицательным полем (`--screen-pad`), то есть
   * теперь в края центрированного столбца — она остаётся ровно под содержимым, а не уезжает
   * к краю окна отдельно от него.
   */
  width: 100%;
  max-width: var(--workspace-max);
  margin-inline: auto;
  /*
   * Контейнер для запросов ниже. Раскладка чтения обязана зависеть от ширины ПАНЕЛИ, а не
   * окна: свернули полосу бэклога — панели прибавилось 320px, и колонки должны разойтись,
   * хотя окно не изменилось ни на пиксель. Медиазапрос этого не видит, контейнерный видит,
   * и панели не приходится ничего знать про состояние соседней полосы.
   */
  container-type: inline-size;
  container-name: draftpanel;
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Снизу отступа нет: последним элементом идёт полоса действий, и её собственный паддинг
     и есть нижнее поле экрана. Отступ здесь оторвал бы приклеенную полосу от края. */
  padding: 18px var(--screen-pad) 0;
  /*
   * СВОЕЙ ПРОКРУТКИ У ПАНЕЛИ НЕТ, И ЭТО ВАЖНО. Раньше здесь стоял `overflow-y: auto`, но он
   * не работал ни дня: высота панели ничем не ограничена (`.app` задаёт min-height, а не
   * height), поэтому панель всегда ровно по содержимому, и прокручивается страница целиком.
   * Мёртвое свойство при этом делало панель scrollport'ом — и `position: sticky` у полосы
   * действий считался от него, то есть не работал вовсе. Убрано намеренно: полоса обязана
   * прилипать к нижнему краю ОКНА.
   */
}

.head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.headline {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

/*
 * ЧТЕНИЕ. Тема и превью встают в две колонки, как только для них хватает места: превью
 * держит свою ширину (оно изображает сообщение Telegram, и растянутое — врёт), а
 * освободившееся место занимает тема. Это и есть ответ на «слишком много пустот» при
 * свёрнутом бэклоге: свернули полосу — панели прибавилось 320px, и колонки разошлись сами,
 * без единого знания о состоянии полосы.
 *
 * 1180px — порог, ниже которого две колонки перестают помещаться (320 минимум темы + 680
 * превью + промежуток). Ниже него — прежний порядок сверху вниз: сначала тема, потом текст.
 */
.reading {
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(0, 680px);
  gap: 20px;
  align-items: start;
}

@container draftpanel (max-width: 1179px) {
  .reading {
    grid-template-columns: minmax(0, 1fr);
  }
}

.narrow {
  max-width: 680px;
  width: 100%;
  margin-inline: auto;
}

.wide {
  max-width: 1180px;
  width: 100%;
  margin-inline: auto;
}

.columns {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
  align-items: start;
}

/* Ниже 1100px две колонки не помещаются: превью встаёт под исходник — ровно туда, куда
   взгляд и так идёт после правки текста. */
@media (max-width: 1099px) {
  .columns {
    grid-template-columns: minmax(0, 1fr);
  }
}

.column {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

/* Подсветка зоны сброса файла. Атрибутом, а не вторым классом: класс пришлось бы склеивать
   в трёх местах вызова, а атрибут ставится одним `data-dropping={dragging ? '' : undefined}`.
   Стоит СНАРУЖИ `.tg` намеренно — правило «внутри превью нет ни одного фирменного токена»
   (ui-design §6) держится ровно до первого исключения. */
.column[data-dropping] {
  outline: 2px dashed var(--violet);
  outline-offset: 6px;
  border-radius: var(--r-card);
}

.versions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Кнопка не наследует шрифт документа — гарнитуру задаём явно. */
.versionBtn {
  font-family: var(--sans);
  font-variant-numeric: tabular-nums;
  font-size: 12px;
  padding: 3px 10px;
  border-radius: var(--r-btn);
  border: 1px solid var(--border);
  background: var(--white);
  color: var(--ink-2);
  cursor: pointer;
}

.versionBtn[aria-pressed="true"] {
  background: var(--violet-bg);
  border-color: var(--violet-bg-2);
  color: var(--accent-ink);
  font-weight: 600;
}

/*
 * Телефон (640). `--screen-pad` объявлен на `.panel` и читается полосой действий, поэтому
 * ужимается ОДНО число, а не два врозь: полоса гасит его отрицательным полем и возвращает
 * своим паддингом, и разъехавшиеся значения оторвали бы её от краёв экрана. 24px с каждой
 * стороны на 390px — это восьмая часть ширины поста.
 */
@media (max-width: 640px) {
  .panel {
    --screen-pad: 12px;
  }
}
